import React from "react";
import type { GrowCardCarouselProps } from "dds/organisms/grow-card-carousel/grow-card-carousel";
import { ZipCodeLazy } from "../v3-zip-code/zip-code-lazy.client";
import { APP_BASE_PATH } from "src/utils/app-base-path";
import { splitZipCopy, type ZipCopyFields } from "../v3-zip-code/zip-copy";
import { anyCardReprices, shouldShowZipCode } from "../v3-zip-code/show-zip-code";

export type GrowSlotProps = GrowCardCarouselProps & ZipCopyFields & {
  /** Forces the ZIP Code on. ⚠ WITHOUT IT THIS CAROUSEL DRAWS NO ZIP: no Grow card can
   *  re-price, so the shared default always answers no here. An author who wants the
   *  control on a Grow carousel must set this. A `false` is not a hide — it defers,
   *  like an absent field; `show-zip-code.ts` carries why. */
  show_zip_code?: boolean | string;
};

/**
 * The Genre Grow carousel's slot props (WAT-11988). `SLOT_FILLERS` runs this before the
 * canvas renders the organism, because the canvas spreads CMS JSON into an organism and
 * JSON cannot carry the node the `zip_code` slot takes. The ZIP arrives through
 * `ZipCodeLazy`, whose `"use client"` boundary gives it its own chunk.
 *
 * ⚠ THE ORGANISM IS A TYPE-ONLY IMPORT, and must stay one. A filler returns props and
 * never renders, so it needs no value. `v3-main-canvas.tsx` imports this module
 * STATICALLY, so a value import here would make `GrowCardCarousel` statically reachable
 * from every v3 page and drag `carousel-shell.client` / `bloom.client` into the shared
 * chunk — silently defeating the `NextDynamic` split `organism-map.ts` exists for.
 * TypeScript will not catch it (strict is off). `slot-fillers.spec.ts` does.
 *
 * SLOT ONLY — deliberately not routed through `toRenderablePlanCard`. Grow cards never
 * receive a `dp_all_in` (no adapter case builds one), their prices are pre-resolved
 * strings, and their `legal_content` is typed `string` on the organism: a React element
 * there would be flattened to "" by `grow-card.tsx`'s `content()` and the legal copy
 * would vanish with no type error. Grow live-pricing is a separate ticket.
 *
 * The local-channels route is prefixed with the app's base path ("/uf" in a production
 * build), as in production (WAT-11617): unprefixed, the fetch 404s on the App Router.
 * The county and channel lookup paths are NOT passed — they are the same relative path
 * in every environment, which the ZIP Code already defaults to.
 *
 * `autoOpenOnFirstVisit` is off. Production opens the panel on a first visit with no
 * known DMA, but the panel is in flow: inside a carousel it expands after hydration and
 * pushes the card track down (`design-conflicts/zip-code.mdx`).
 *
 * No `store` prop: a real store (functions) cannot cross from a server component, nor
 * should CMS data reach one. Tests inject a store by mocking `ZipCodeConnected`.
 * An authored `zip_code` is dropped — off the wire it can only be a string; the visible
 * copy comes from the flat `zip_*` fields (`ZipCopyFields`).
 */
export function fillGrowSlots({
  zip_code: _authoredZipCode,
  show_zip_code: showZipCode,
  ...props
}: GrowSlotProps): GrowCardCarouselProps {
  const { copy, rest: carousel } = splitZipCopy(props);
  /* `auto` is the SAME re-pricing test as Plan and HP — one rule across all three, so
     the CMS team reads one sentence rather than three.

     ⚠ IT ALWAYS ANSWERS NO HERE, and that is the intended outcome, not an oversight.
     No adapter case builds a `dp_all_in` for a Grow card and their prices are
     pre-resolved strings, in production too, so a Grow carousel draws the ZIP ONLY when
     an author sets `show_zip_code`. That is a deliberate change from WAT-11988, where
     the slot was unconditional: a control that cannot change a number on the carousel
     it sits in should be asked for, not assumed. ⚠ The field must exist on the blok
     before this ships, or no author can ask. */
  const showZip = shouldShowZipCode(showZipCode, anyCardReprices(carousel?.cards));
  return {
    ...carousel,
    zip_code: showZip ?
      <ZipCodeLazy {...copy} autoOpenOnFirstVisit={false} localChannelsBasePath={APP_BASE_PATH} /> :
      undefined
  } as GrowCardCarouselProps;
}

export default fillGrowSlots;
